import { useState } from "react";
import { useMediaQuery } from "@mui/material";
import { Icon } from "../components/liquid/LiquidPrimitives";
import { useEditableFaqsPage } from "../templates/templateConfigContext";
import { useTemplateConfig } from "../templates/templateConfigContext";
import { useTiendaInfo } from "../contexts/TiendaInfoContext";

function AccordionItem({
    pregunta,
    respuesta,
    open,
    onToggle,
}: {
    pregunta: string;
    respuesta: string;
    open: boolean;
    onToggle: () => void;
}) {
    return (
        <div style={{ borderBottom: "1px solid var(--divider)" }}>
            <button
                type="button"
                onClick={onToggle}
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 16,
                    padding: "18px 0",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                }}
            >
                <span
                    style={{
                        fontSize: 15,
                        fontWeight: 600,
                        letterSpacing: "-0.01em",
                        color: open ? "var(--gold)" : "var(--ink-900)",
                        lineHeight: 1.4,
                        transition: "color 0.2s",
                    }}
                >
                    {pregunta}
                </span>
                <span
                    style={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: 999,
                        background: open ? "var(--gold)" : "var(--cream-200)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: open ? "var(--btn-dark-text)" : "var(--gold)",
                        transition: "background 0.2s, transform 0.2s",
                        transform: open ? "rotate(45deg)" : "none",
                    }}
                >
                    <Icon.plus />
                </span>
            </button>
            {open && (
                <div
                    style={{
                        paddingBottom: 20,
                        fontSize: 14,
                        lineHeight: 1.8,
                        color: "var(--ink-500)",
                        maxWidth: 680,
                    }}
                >
                    {respuesta}
                </div>
            )}
        </div>
    );
}

export default function FAQsPage() {
    const isSmall = useMediaQuery("(max-width:900px)");
    const { config } = useTemplateConfig();
    const tiendaInfo = useTiendaInfo();
    useEditableFaqsPage();
    const faqs = config.faqs ?? [];
    const vchEyebrow = config.faqsConfig?.vchEyebrow ?? "Preguntas frecuentes";
    const vchTitulo = config.faqsConfig?.vchTitulo ?? "¿En qué podemos ayudarte?";
    const vchSubtitulo =
        config.faqsConfig?.vchSubtitulo ??
        "Encontrá respuestas a las preguntas más frecuentes sobre nuestros productos y servicios.";
    const [openIdx, setOpenIdx] = useState<number | null>(null);
    const waNumero = tiendaInfo?.whatsappNumero;

    const contactCard = (
        <div
            style={{
                padding: "20px 20px",
                borderRadius: "var(--radius-md, 16px)",
                background: "var(--cream-200)",
                border: "1px solid var(--divider)",
                textAlign: isSmall ? "center" : "left",
            }}
        >
            <div
                style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: "var(--ink-900)",
                    marginBottom: 6,
                }}
            >
                ¿No encontrás tu respuesta?
            </div>
            <div
                style={{
                    fontSize: 13,
                    color: "var(--ink-500)",
                    lineHeight: 1.6,
                    marginBottom: waNumero ? 14 : 0,
                }}
            >
                Contactanos directamente y te ayudamos.
            </div>
            {waNumero && (
                <a
                    href={`https://wa.me/${waNumero}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "8px 16px",
                        borderRadius: 999,
                        background: "var(--btn-dark-bg)",
                        color: "var(--btn-dark-text)",
                        fontSize: 13,
                        fontWeight: 600,
                        textDecoration: "none",
                    }}
                >
                    <Icon.whatsapp /> WhatsApp
                </a>
            )}
        </div>
    );

    return (
        <main style={{ position: "relative", zIndex: 2 }}>
            {/* Header */}
            <section style={{ padding: isSmall ? "16px 16px 0" : "24px 32px 0" }}>
                <div
                    style={{
                        borderRadius: isSmall ? 24 : 32,
                        background:
                            "linear-gradient(130deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.65) 100%)",
                        padding: isSmall ? "40px 28px" : "56px 72px",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            pointerEvents: "none",
                            backgroundImage:
                                "linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)",
                            backgroundSize: "48px 48px",
                        }}
                    />
                    <div style={{ position: "relative" }}>
                        <div
                            style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: "rgba(255,255,255,0.55)",
                                marginBottom: 12,
                            }}
                        >
                            {vchEyebrow}
                        </div>
                        <h1
                            style={{
                                fontSize: isSmall ? 34 : 52,
                                fontWeight: 700,
                                letterSpacing: "-0.03em",
                                lineHeight: 1.05,
                                color: "#ffffff",
                                margin: 0,
                            }}
                        >
                            {vchTitulo}
                        </h1>
                        <p
                            style={{
                                fontSize: 15,
                                color: "rgba(255,255,255,0.55)",
                                marginTop: 14,
                                lineHeight: 1.6,
                                maxWidth: 440,
                            }}
                        >
                            {vchSubtitulo}
                        </p>
                    </div>
                </div>
            </section>

            {/* Contenido */}
            <section
                style={{
                    padding: isSmall ? "32px 16px 64px" : "48px 32px 80px",
                    maxWidth: 860,
                    margin: "0 auto",
                }}
            >
                {faqs.length === 0 ? (
                    <div
                        style={{
                            textAlign: "center",
                            padding: "60px 0",
                            color: "var(--ink-500)",
                            fontSize: 14,
                        }}
                    >
                        Próximamente encontrarás las preguntas frecuentes aquí.
                    </div>
                ) : (
                    <div
                        style={{
                            borderRadius: "var(--radius-md, 20px)",
                            background: "var(--glass-bg)",
                            border: "1px solid var(--divider)",
                            padding: "0 24px",
                            marginBottom: 24,
                        }}
                    >
                        {faqs.map((item, idx) => (
                            <AccordionItem
                                key={idx}
                                pregunta={item.vchPregunta}
                                respuesta={item.vchRespuesta}
                                open={openIdx === idx}
                                onToggle={() => setOpenIdx((prev) => (prev === idx ? null : idx))}
                            />
                        ))}
                    </div>
                )}

                {contactCard}
            </section>
        </main>
    );
}
